/**
 * Browser Sentry: Web Vitals + request tracing + user/auth context + request-id breadcrumbs.
 * Config: app/views/shared/_sentry_browser_config.html.erb (window.__AIFACTORY_SENTRY__).
 * User: body data-sentry-user / data-current-user-* / data-posthog-user (synced on Turbo).
 * Legacy pages are tagged app.surface=legacy so Insights / metric alerts can cover the whole shell.
 */
import * as Sentry from "@sentry/react"

const isBrowser = typeof window !== "undefined"
let booted = false
let fetchInstrumented = false

const SENTRY_SOURCE_ATTR =
  (typeof Sentry.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE === "string" &&
    Sentry.SEMANTIC_ATTRIBUTE_SENTRY_SOURCE) ||
  "sentry.source"

/** Paths served by LegacyController (fullscreen React shell). */
export function isLegacySurfacePath(pathname) {
  const path = String(pathname || "").split("?")[0]
  if (path === "/legacy" || path === "/legacy_home" || path.startsWith("/legacy/")) return true
  if (path === "/user" || path.startsWith("/user/")) return true
  if (path === "/corpora" || path.startsWith("/corpora/")) return true
  if (/^\/accounts\/[^/]+\/corpora(\/|$)/.test(path)) return true
  return false
}

export function legacySurfaceForPath(pathname) {
  return isLegacySurfacePath(pathname) ? "legacy" : "other"
}

/** Collapse dynamic segments so pageloads group in Web Vitals / traces. */
export function parameterizeTransactionName(pathname) {
  const path = String(pathname || "").split("?")[0] || "/"
  return path
    .replace(
      /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi,
      "/:uuid",
    )
    .replace(/\/[a-f0-9]{32}(?=\/|$)/g, "/:hash")
    .replace(/\/\d+(?=\/|$)/g, "/:id")
}

export function buildBrowserSpanContext(context, pathname) {
  const path =
    pathname ||
    (typeof location !== "undefined" ? location.pathname : "") ||
    context?.name ||
    "/"
  const name = parameterizeTransactionName(path)
  const surface = legacySurfaceForPath(path)
  return {
    ...context,
    name,
    attributes: {
      ...(context?.attributes || {}),
      "app.surface": surface,
      [SENTRY_SOURCE_ATTR]: name !== path ? "route" : "url",
    },
  }
}

function originRegex() {
  if (typeof location === "undefined") return /^$/
  return new RegExp(`^${location.origin.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`)
}

function compactText(value) {
  return String(value ?? "")
    .replace(/\s+/g, " ")
    .trim()
}

function safePreview(value, max = 140) {
  const compact = compactText(value)
  if (compact.length <= max) return compact
  return `${compact.slice(0, max - 1)}…`
}

function createRequestId(prefix = "af") {
  const cryptoObj = globalThis.crypto
  if (cryptoObj && typeof cryptoObj.randomUUID === "function") {
    return `${prefix}-${cryptoObj.randomUUID()}`
  }
  if (cryptoObj && typeof cryptoObj.getRandomValues === "function") {
    const bytes = cryptoObj.getRandomValues(new Uint8Array(12))
    const suffix = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("")
    return `${prefix}-${suffix}`
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`
}

function readUserFromDOM() {
  if (!isBrowser || !document?.body) return null
  const body = document.body
  const ds = body.dataset || {}

  // Prefer explicit sentry payload (includes impersonation).
  if (ds.sentryUser) {
    try {
      const parsed = JSON.parse(ds.sentryUser)
      if (parsed?.id) return parsed
    } catch (_) {
      /* ignore */
    }
  }

  if (ds.currentUserId) {
    return {
      id: String(ds.currentUserId),
      email: ds.currentUserEmail || null,
      account_id: ds.effectiveAccountId || null,
      solution_slug: ds.currentSolutionSlug || null,
      solution_id: ds.currentSolutionId || null,
      role: ds.currentUserRole || (ds.superAdmin === "true" ? "super_admin" : "user"),
      impersonating: ds.impersonating === "true",
    }
  }

  // Fallback: PostHog identify payload (skipped during impersonation on server).
  if (ds.posthogUser) {
    try {
      const ph = JSON.parse(ds.posthogUser)
      const props = ph?.properties || {}
      const id = ph?.distinctId || props.email
      if (!id) return null
      return {
        id: String(props.email || id),
        email: props.email || String(id),
        role: props.super_admin ? "super_admin" : "user",
        impersonating: false,
      }
    } catch (_) {
      return null
    }
  }

  return null
}

function applySurfaceContext() {
  if (!booted || !isBrowser) return
  const pathname = typeof location !== "undefined" ? location.pathname : ""
  Sentry.setTag("app.surface", legacySurfaceForPath(pathname))
}

/** Impersonator email for Sentry User + tags. Null when not impersonating. */
export function impersonatedByFromUser(user) {
  if (!user || !user.impersonating) return null
  const email = compactText(user.impersonated_by || "")
  return email || null
}

/** Sentry.setUser payload: current_user identity, plus impersonated_by when set. */
export function sentryUserForClient(user) {
  if (!user) return null
  const payload = {
    id: String(user.id),
    email: user.email || undefined,
    username: user.email || undefined,
  }
  const impersonatedBy = impersonatedByFromUser(user)
  if (impersonatedBy) payload.impersonated_by = impersonatedBy
  return payload
}

function applyUserContext() {
  if (!booted) return
  applySurfaceContext()
  const user = readUserFromDOM()
  if (!user) {
    Sentry.setUser(null)
    Sentry.setTag("auth.role", "anonymous")
    Sentry.setTag("auth.impersonating", "false")
    Sentry.setTag("auth.impersonated_by", undefined)
    Sentry.setContext("auth", null)
    return
  }

  const impersonatedBy = impersonatedByFromUser(user)
  Sentry.setUser(sentryUserForClient(user))
  Sentry.setTag("auth.role", user.role || "user")
  Sentry.setTag("auth.impersonating", user.impersonating ? "true" : "false")
  Sentry.setTag("auth.impersonated_by", impersonatedBy || undefined)
  if (user.account_id) Sentry.setTag("auth.account_id", String(user.account_id))
  if (user.solution_slug) Sentry.setTag("auth.solution_slug", String(user.solution_slug))
  if (user.solution_id) Sentry.setTag("auth.solution_id", String(user.solution_id))
  Sentry.setContext("auth", {
    id: user.id,
    email: user.email || null,
    account_id: user.account_id || null,
    solution_slug: user.solution_slug || null,
    solution_id: user.solution_id || null,
    role: user.role || "user",
    impersonating: Boolean(user.impersonating),
    impersonated_by: impersonatedBy,
  })
}

export function addSentryBreadcrumb(category, message, data, level = "info") {
  if (!booted) return
  Sentry.addBreadcrumb({
    category,
    message: safePreview(message, 220),
    data,
    level,
  })
}

export function withRequestId(headers = {}, meta) {
  const merged =
    headers instanceof Headers
      ? Object.fromEntries(headers.entries())
      : Object.fromEntries(Object.entries(headers || {}).map(([k, v]) => [k, String(v)]))
  const existing = compactText(merged["X-Request-Id"] || merged["x-request-id"])
  const requestId = existing || createRequestId()
  merged["X-Request-Id"] = requestId
  if (meta) {
    addSentryBreadcrumb(`${meta.category || "http"}.request`, meta.action || "request", {
      request_id: requestId,
      method: meta.method || "GET",
      url: meta.url,
      ...(meta.extra || {}),
    })
  }
  return { headers: merged, requestId }
}

export function noteHttpResponse(meta = {}) {
  const headerValue =
    meta.response?.headers?.get?.("X-Request-Id") ||
    meta.response?.headers?.get?.("x-request-id")
  const resolved = compactText(headerValue) || meta.requestId
  addSentryBreadcrumb(
    `${meta.category || "http"}.response`,
    meta.action || "response",
    {
      request_id: resolved,
      method: meta.method || "GET",
      status: meta.response?.status,
      url: meta.url,
      ...(meta.extra || {}),
    },
    meta.level || (meta.response && meta.response.status >= 400 ? "warning" : "info"),
  )
  return resolved
}

function toCapturedError(error) {
  if (error instanceof Error) return error
  if (error && typeof error === "object") {
    const message = compactText(error.data?.message || error.statusText || error.message || "")
    const err = new Error(message || String(error))
    if (error.status != null) err.name = "RouteError"
    return err
  }
  return new Error(String(error))
}

/** React Router 404s are handled in-app; do not open Sentry issues for them. */
export function isIgnorableRouteError(error) {
  return Boolean(error && typeof error === "object" && Number(error.status) === 404)
}

/**
 * Capture a browser exception that an in-app UI already caught (route errorElement,
 * etc.). Caught React render errors do not hit window.onerror, so the SDK's
 * global handler never sees them unless we report here.
 */
export function captureBrowserException(error, context = {}) {
  if (!booted) return
  if (isIgnorableRouteError(error)) return
  const err = toCapturedError(error)
  const component = compactText(context.component || "browser") || "browser"
  const phase = compactText(context.phase || "")
  const source = compactText(context.source || "")
  const detail = safePreview(context.detail || err.message || "", 220)
  const pathname =
    compactText(context.pathname || (typeof location !== "undefined" ? location.pathname : "")) || null

  Sentry.withScope((scope) => {
    scope.setTag("service", "browser")
    scope.setTag("component", component)
    scope.setTag(
      "app.surface",
      context.surface || legacySurfaceForPath(pathname || ""),
    )
    if (phase) scope.setTag("error.phase", phase)
    if (source) scope.setTag("error.source", source)
    if (error && typeof error === "object" && error.status != null) {
      scope.setTag("http.status_code", String(error.status))
    }
    scope.setContext("browser_error", {
      component,
      phase: phase || null,
      source: source || null,
      detail,
      pathname,
      user: readUserFromDOM(),
    })
    Sentry.captureException(err)
  })
}

/** Render crashes share one path from the root error handler and componentDidCatch. */
const reportedRunwayFailures = new WeakSet()

function sentryClientReady() {
  if (booted) return true
  if (typeof Sentry.getClient === "function" && Sentry.getClient()) return true
  initSentryBrowser()
  return booted || Boolean(typeof Sentry.getClient === "function" && Sentry.getClient())
}

function normalizeFingerprintPart(value) {
  return compactText(value)
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ":uuid")
    .replace(/\b\d+\b/g, ":n")
    .slice(0, 180)
}

/** Page-load crashes must not collapse into one handled issue. */
export function runwayFailureIsPageCrash(context = {}) {
  const phase = compactText(context.phase || "")
  return phase === "error_boundary" || phase === "mount"
}

export function runwayFailureFingerprint(error, context = {}) {
  const phase = compactText(context.phase || "")
  const code = compactText(context.code || "")
  const step = compactText(context.step || "")
  const runId = compactText(context.runId || context.run_id || "")
  const err = error instanceof Error ? error : new Error(String(error ?? ""))
  if (runwayFailureIsPageCrash(context)) {
    return [
      "aifactory-runway-browser",
      phase,
      normalizeFingerprintPart(context.source || "browser") || "browser",
      normalizeFingerprintPart(err.name || "Error") || "Error",
      normalizeFingerprintPart(err.message || "unknown") || "unknown",
    ]
  }
  return [
    "aifactory-runway-browser",
    code || step || phase || "unknown",
    runId || "unknown",
  ]
}

/**
 * Capture a runway / langchain client-side failure with structured tags + fingerprint.
 * Page crashes are unhandled and grouped by exception type and message.
 */
export function captureRunwayFailure(error, context = {}) {
  if (!sentryClientReady()) return
  if (error && typeof error === "object") {
    if (reportedRunwayFailures.has(error)) return
    reportedRunwayFailures.add(error)
  }
  const err = error instanceof Error ? error : new Error(String(error))
  const runId = compactText(context.runId || context.run_id || "")
  const workflow = compactText(context.workflow || context.graphName || "")
  const phase = compactText(context.phase || "")
  const step = compactText(context.step || "")
  const code = compactText(context.code || "")
  const requestId = compactText(context.requestId || context.request_id || "")
  const detail = safePreview(context.detail || err.message || "", 220)
  const pageCrash = runwayFailureIsPageCrash(context)

  Sentry.withScope((scope) => {
    scope.setTag("runway.failure", "true")
    scope.setTag("service", "browser")
    scope.setTag("component", "runway")
    scope.setTag("app.surface", "legacy")
    scope.setTag("error.type", err.name || "Error")
    if (runId) scope.setTag("runway.run_id", runId)
    if (workflow) scope.setTag("runway.workflow", workflow)
    if (phase) scope.setTag("runway.phase", phase)
    if (step) scope.setTag("runway.step", step)
    if (code) scope.setTag("runway.error_code", code)
    if (requestId) scope.setTag("request_id", requestId)
    if (context.statusCode != null) scope.setTag("runway.status_code", String(context.statusCode))
    scope.setContext("runway", {
      runId: runId || null,
      workflow: workflow || null,
      phase: phase || null,
      step: step || null,
      code: code || null,
      detail,
      requestId: requestId || null,
      statusCode: context.statusCode ?? null,
      source: context.source || "browser_runway",
      user: readUserFromDOM(),
    })
    scope.setFingerprint(runwayFailureFingerprint(err, context))
    if (pageCrash && typeof Sentry.captureReactException === "function") {
      Sentry.captureReactException(
        err,
        { componentStack: typeof context.detail === "string" ? context.detail : "" },
        { mechanism: { type: "react.error_boundary", handled: false } },
      )
      return
    }
    Sentry.captureException(
      err,
      pageCrash ? { mechanism: { type: "react.error_boundary", handled: false } } : undefined,
    )
  })
}

function installFetchInstrumentation() {
  if (!isBrowser || fetchInstrumented || typeof window.fetch !== "function") return
  fetchInstrumented = true
  const nativeFetch = window.fetch.bind(window)
  window.fetch = async (input, init = {}) => {
    let url = ""
    try {
      url = typeof input === "string" ? input : input?.url || ""
    } catch (_) {
      url = ""
    }
    const sameOrigin =
      !url ||
      url.startsWith("/") ||
      (typeof location !== "undefined" && url.startsWith(location.origin))

    if (!sameOrigin || !booted) {
      return nativeFetch(input, init)
    }

    const method = (init.method || (typeof input !== "string" && input?.method) || "GET").toUpperCase()
    const { headers, requestId } = withRequestId(init.headers || {}, {
      category: "http",
      action: `${method} ${safePreview(url, 120)}`,
      method,
      url: safePreview(url, 180),
    })
    const nextInit = { ...init, headers }
    try {
      const response = await nativeFetch(input, nextInit)
      noteHttpResponse({
        category: "http",
        action: `${method} ${safePreview(url, 120)}`,
        method,
        url: safePreview(url, 180),
        requestId,
        response,
      })
      return response
    } catch (error) {
      const errorMessage = String(error?.message || error || "")
      const isAbort = error && typeof error === "object" && (error.name === "AbortError" || error.code === 20)
      const isTransientNetworkError =
        errorMessage.includes("Load failed") ||
        errorMessage.includes("Failed to fetch") ||
        errorMessage.includes("NetworkError") ||
        errorMessage.includes("network error")

      if (isAbort) {
        addSentryBreadcrumb(
          "http.abort",
          `${method} ${safePreview(url, 120)} aborted`,
          { request_id: requestId, url: safePreview(url, 180) },
          "info",
        )
      } else {
        addSentryBreadcrumb(
          isTransientNetworkError ? "http.network_error" : "http.error",
          `${method} ${safePreview(url, 120)} failed`,
          { request_id: requestId, url: safePreview(url, 180), error: errorMessage },
          isTransientNetworkError ? "info" : "error",
        )
      }

      if (!isAbort && !isTransientNetworkError) {
        Sentry.withScope((scope) => {
          scope.setTag("service", "browser")
          scope.setTag("component", "http")
          scope.setTag("http.method", method)
          scope.setContext("http_error", {
            method,
            url: safePreview(url, 180),
            requestId,
            error: errorMessage,
          })
          Sentry.captureException(error)
        })
      }

      throw error
    }
  }
}

export function initSentryBrowser() {
  if (!isBrowser || booted) return
  const cfg = window.__AIFACTORY_SENTRY__
  if (!cfg || !cfg.dsn) return

  const rate =
    typeof cfg.tracesSampleRate === "number" && !Number.isNaN(cfg.tracesSampleRate)
      ? cfg.tracesSampleRate
      : 0.2

  Sentry.init({
    dsn: cfg.dsn,
    environment: cfg.environment || "production",
    release: cfg.release || undefined,
    integrations: [
      Sentry.browserTracingIntegration({
        enableInp: true,
        instrumentPageLoad: true,
        instrumentNavigation: true,
        tracePropagationTargets: [originRegex(), /^\//],
        beforeStartSpan: (context) => buildBrowserSpanContext(context),
      }),
    ],
    tracesSampleRate: rate,
    sendDefaultPii: true,
    beforeSend(event, hint) {
      const error = hint?.originalException
      if (error && typeof error === "object") {
        if (error.name === "AbortError" || error.code === 20) {
          return null
        }
      }
      return event
    },
    initialScope: {
      tags: {
        service: "browser",
        component: "browser",
        "app.surface": legacySurfaceForPath(typeof location !== "undefined" ? location.pathname : ""),
      },
    },
  })
  booted = true
  applyUserContext()
  installFetchInstrumentation()
}

export function syncSentryUser() {
  applyUserContext()
}

export const SentryClient = {
  init: initSentryBrowser,
  syncUser: syncSentryUser,
  addBreadcrumb: addSentryBreadcrumb,
  createRequestId,
  withRequestId,
  noteHttpResponse,
  captureRunwayFailure,
  runwayFailureFingerprint,
  runwayFailureIsPageCrash,
  captureBrowserException,
  isIgnorableRouteError,
  isLegacySurfacePath,
  legacySurfaceForPath,
  parameterizeTransactionName,
  buildBrowserSpanContext,
  impersonatedByFromUser,
  sentryUserForClient,
}

initSentryBrowser()

if (isBrowser) {
  document.addEventListener("DOMContentLoaded", syncSentryUser)
  document.addEventListener("turbo:load", syncSentryUser)
}
